import { ExistingSystemType } from 'data/types';
import {
  DuctedCompatibilityEntry,
  DuctedRepresentativeEntry,
  DuctedResultSet
} from 'features/ducted-compatibility';
import { DuctedSystemTypeSelection } from 'features/ui/ui-slice';
import { facetCodeByLabel } from 'utils/ducted-rail-selections';
import {
  DuctedFuelToken,
  systemTypeCodeFuel
} from 'utils/kept-system-type-annotation';
import { SystemSortOption } from 'utils/system-sort';
import {
  boundedCodesFor,
  displayableSystemTypes,
  filterSystemTypesByFuel,
  orderSystemTypesByCodes,
  scopedDuctedTypeCodes
} from './ducted-facet-merge';
import {
  buildDuctedRankGroups,
  summaryLabelsFor
} from './ducted-rail-rank-groups';
import {
  DuctedRailSectionModel,
  DuctedRailSubSectionModel
} from './ducted-rail-sections-types';

export type DuctedRailSectionInputs = {
  resultsBySystem: Record<
    string,
    Partial<Record<DuctedResultSet, DuctedCompatibilityEntry>>
  >;
  representativesBySystem: Record<
    string,
    Partial<Record<DuctedResultSet, Record<string, DuctedRepresentativeEntry>>>
  >;
  systemTypeSelections: DuctedSystemTypeSelection[];
  fuelSelections: DuctedFuelToken[];
  systemSort: SystemSortOption;
  equipmentMatchingOnly?: boolean;
  compatibleResultsBySystem: Record<
    string,
    | {
        kept?: { systemTypes: Array<{ code: string; label: string }> };
        alternatives?: { systemTypes: Array<{ code: string; label: string }> };
      }
    | undefined
  >;
};

const buildSubSection = (
  system: ExistingSystemType,
  resultSet: DuctedResultSet,
  priorityCodes: string[],
  inputs: DuctedRailSectionInputs
): DuctedRailSubSectionModel => {
  const {
    resultsBySystem,
    representativesBySystem,
    systemTypeSelections,
    fuelSelections
  } = inputs;
  const entry = resultsBySystem[system.uniqueId]?.[resultSet];
  const representatives =
    representativesBySystem[system.uniqueId]?.[resultSet] ?? {};
  // The System Type selection scoped to THIS result set (AHA-6681
  // filter split): while any selection is active, each set shows
  // only its own scoped codes — possibly none.
  const scoped = scopedDuctedTypeCodes(systemTypeSelections, resultSet);
  // The Fuel chips filter display STRICTLY (AHA-6796): the fetch
  // collapse-widens an all-miss fuel selection, but the rail must
  // never leak a wrong-fuel row — possibly zero rows.
  const fuelActive = fuelSelections.length > 0;
  const selectedFuels = new Set(fuelSelections);
  const displayTypes = orderSystemTypesByCodes(
    filterSystemTypesByFuel(
      displayableSystemTypes(entry?.systemTypes ?? [], scoped),
      fuelSelections
    ),
    priorityCodes
  );
  const representativeEntries = displayTypes.map((systemType) => ({
    systemType,
    representative: representatives[systemType.code]
  }));
  const representativeIds = new Set<unknown>(
    representativeEntries
      .map(({ representative }) => representative?.combination?.ID)
      .filter((id) => id != null)
  );
  // Tail = loaded pages minus the representative rows, server order.
  // While a type or fuel selection is active, additionally
  // client-filter the tail to the scoped codes / selected fuels via
  // the facet's label→code map — the fetch may have collapse-
  // widened to the full bound (disjoint chip or all-miss fuel
  // selection), and rows whose label resolves no code are dropped
  // rather than shown against an unverifiable type.
  const scopedCodes = new Set(scoped.codes);
  const tailCodeByLabel = facetCodeByLabel(entry?.systemTypes ?? []);
  const tail = (entry?.combinations ?? []).filter((combination) => {
    if (representativeIds.has(combination.ID)) return false;
    if (!scoped.active && !fuelActive) return true;
    const code = tailCodeByLabel.get(
      (combination.SystemTypeLabel as string) ?? ''
    );
    if (code === undefined) return false;
    if (scoped.active && !scopedCodes.has(code)) return false;
    return !fuelActive || selectedFuels.has(systemTypeCodeFuel(code));
  });
  // Facet counts are selection-independent server-side, so the
  // scoped/fuel sum is the exact per-set total under an active
  // selection.
  const displayTotalCount =
    scoped.active || fuelActive
      ? (entry?.systemTypes ?? [])
          .filter(
            (st) =>
              (!scoped.active || scopedCodes.has(st.code)) &&
              (!fuelActive || selectedFuels.has(systemTypeCodeFuel(st.code)))
          )
          .reduce((sum, st) => sum + st.count, 0)
      : entry?.totalCount ?? 0;
  const scopedEmpty =
    (scoped.active || fuelActive) &&
    entry?.status === 'loaded' &&
    displayTotalCount === 0 &&
    entry.totalCount > 0;
  return {
    resultSet,
    entry,
    representativeEntries,
    tail,
    displayTotalCount,
    scopedEmpty
  };
};

// The result sets a section renders: ≥1 kept component puts the
// kept-constrained set in play next to the full-replacement alternatives;
// the Equipment Matching filter keeps ONLY the kept set, so an all-replacing
// system (which has no kept pairings) renders nothing while it is on.
export const ductedRailResultSets = (
  hasKept: boolean,
  equipmentMatchingOnly: boolean
): DuctedResultSet[] => {
  if (equipmentMatchingOnly) return hasKept ? ['kept'] : [];
  return hasKept ? ['kept', 'alternatives'] : ['alternatives'];
};

export const buildDuctedRailSection = (
  system: ExistingSystemType,
  inputs: DuctedRailSectionInputs
): DuctedRailSectionModel => {
  // ≥1 kept component ⇒ the kept-constrained set is in play and the
  // section splits into the two labeled sub-sections; all-replacing
  // systems have only the alternatives set and render unlabeled.
  const hasKept = system.components.some((component) => !component.replacing);
  // Kept component classes feed the "Customer's <equipment>" display
  // titles (same derivation as the EE step's kept-column annotation).
  const keptComponentTypes = system.components
    .filter((component) => !component.replacing)
    .map((component) => component.type);
  // One priority bound per system, shared by BOTH sub-sections: the
  // LES-4 summary's codes carry recommendation priority order, which
  // the default panel renders in (AHA-6681); no summary (null) means
  // no reorder — the slice's server order stands.
  const summary = inputs.compatibleResultsBySystem[system.uniqueId];
  const priorityCodes = boundedCodesFor(summary) ?? [];
  const resultSets = ductedRailResultSets(
    hasKept,
    inputs.equipmentMatchingOnly ?? false
  );
  const subSections = resultSets.map((resultSet) =>
    buildSubSection(system, resultSet, priorityCodes, inputs)
  );
  return {
    system,
    hasKept,
    keptComponentTypes,
    subSections,
    rankGroups: buildDuctedRankGroups({
      systemUniqueId: system.uniqueId,
      subSections,
      priorityCodes,
      summaryLabelByCode: summaryLabelsFor(summary),
      systemTypeSelections: inputs.systemTypeSelections,
      fuelSelections: inputs.fuelSelections,
      systemSort: inputs.systemSort
    })
  };
};

// Active tonnage filter (AHA-6681 tonnage §C polish): furnace-only combos
// null-pass the size filter server-side, so cards without an outdoor unit
// carry the "No cooling side" explanation ONLY while the filter is on.
export const isNoCoolingSideCombination = (
  combination: Record<string, unknown>,
  tonnageFilterActive: boolean
): boolean =>
  tonnageFilterActive &&
  !combination.OutdoorManufacturer &&
  !combination.OutdoorModelNumber;

export const hasDuctedTonnageFilter = (
  filters: ReadonlyArray<{ field: string }>
): boolean => filters.some((filter) => filter.field === 'tonnage');
